import Card from '../card/Card';
import Label from '../texts/label/Label';
import P from '../texts/p/P';
import { useClientTheme } from '../../context/ThemeContext';
import theme from '../../styles/theme';

interface IStepProps {
  widthContainer: string;
  widthCircle1: string;
  widthCircle2: string;
  widthId: string;
  heightId: string;
  step: {
    id: number;
    value: string;
  };
  index: number;
  currentStep: number;
  changeStep: (stepId: number) => void;
  stepsLength: number;
  /**
   * Atlantic / Cem UY Figma (proto photo 2026-08-18):
   * active = white fill + thick dark ring + dark numeral + soft grey halo
   * inactive = white fill + thin light ring + weak numeral
   * Parent owns connector line; title ABOVE stepper (never same row).
   */
  atlanticStyle?: boolean;
}

const Step = ({
  widthContainer,
  widthCircle1,
  widthCircle2,
  widthId,
  heightId,
  step,
  index,
  currentStep,
  stepsLength,
  changeStep,
  atlanticStyle = false,
}: IStepProps) => {
  const { clientTheme } = useClientTheme();
  const isActive = currentStep === step.id;

  const circleVariant = isActive ? (clientTheme ? 'primary' : 'success') : 'neutral';
  const numeralModifier = isActive ? 'strong' : 'weak';

  const atlanticActiveBorder = theme.colors['secondary-2'].text.regular;
  const atlanticInactiveBorder = theme.colors.neutral.border.weak;
  const atlanticInactiveNumeral = theme.colors.neutral.text.weak;
  const atlanticActiveNumeral = theme.colors['secondary-2'].text.regular;

  if (atlanticStyle) {
    return (
      <div
        style={{
          flex: '1 1 0',
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <div
          role='button'
          tabIndex={0}
          onClick={() => changeStep(index + 1)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') changeStep(index + 1);
          }}
          style={{
            width: widthId,
            height: widthId,
            minWidth: widthId,
            borderRadius: '50%',
            boxSizing: 'border-box',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            background: theme.colors.neutral.bg.inverted,
            border: isActive
              ? `2px solid ${atlanticActiveBorder}`
              : `1px solid ${atlanticInactiveBorder}`,
            boxShadow: isActive
              ? `0 0 0 4px ${theme.colors.neutral.bg.muted}`
              : 'none',
            color: isActive ? atlanticActiveNumeral : atlanticInactiveNumeral,
            flexShrink: 0,
            position: 'relative',
            zIndex: 1,
          }}
        >
          <span
            style={{
              fontSize: 15,
              fontWeight: isActive ? 700 : 500,
              lineHeight: 1,
              fontFamily: 'Inter, sans-serif',
              color: isActive ? atlanticActiveNumeral : atlanticInactiveNumeral,
            }}
          >
            {step.id}
          </span>
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            marginTop: 8,
            textAlign: 'center',
            padding: '0 6px',
            maxWidth: '100%',
            lineHeight: 1.15,
            // Avoid nowrap: long UY labels ("Actividad post-balance") overflow and overlap
            whiteSpace: 'normal',
            overflowWrap: 'break-word',
          }}
        >
          <span
            style={{
              color: theme.colors['secondary-2'].text.regular,
              fontFamily: 'Inter, sans-serif',
              fontSize: 12,
              fontWeight: isActive ? 700 : 400,
              lineHeight: '14px',
            }}
          >
            {step.value}
          </span>
        </div>
      </div>
    );
  }

  return (
    <Card width={widthContainer}>
      <Card display='flex' alignItems='center'>
        <Card
          width={widthCircle1}
          height='2px'
          variant='neutral'
          modifier={index === 0 ? 'inverted' : 'weak'}
          borderRadiusAmount='1'
        ></Card>
        <Card
          variant={circleVariant as any}
          modifier='inverted'
          spacing='2'
          width={widthId}
          height={heightId}
          borderRadiusAmount='5'
          border
          display='flex'
          alignItems='center'
          justifyContent='center'
          onClick={() => changeStep(index + 1)}
        >
          <P
            align='center'
            variant='neutral'
            modifier={numeralModifier as any}
            withMargin={false}
          >
            {step.id}
          </P>
        </Card>
        <Card
          width={widthCircle2}
          height='2px'
          variant='neutral'
          modifier={index + 1 === stepsLength ? 'inverted' : 'weak'}
          borderRadiusAmount='1'
        ></Card>
      </Card>

      <Card display='flex' justifyContent='center' margin={{ top: '2' }}>
        <Label type='4' weight='bold' variant='neutral' modifier='regular'>
          {step.value}
        </Label>
      </Card>
    </Card>
  );
};
export default Step;
